import React from 'react';
import { Box, Tooltip, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calculateDeadlineProgress } from '../../../../utils/deadlineProgressUtils';

/**
 * DeadlineCell - Shows progress towards due date from scheduled date
 * Progress starts at 0% on scheduled_date and reaches 100% at due_date or completion_date
 * If completion_date exists, progress stops at that date
 * Only shows progress if current date is after scheduled_date
 */
export default function DeadlineCell({ row }) {
  const { t } = useTranslation(['tasks']);
  const dueDate = row.due_date ? new Date(row.due_date) : null;
  const completionDate = row.completion_date ? new Date(row.completion_date) : null;

  if (!dueDate) {
    return null;
  }

  const { percent: progressPercent, barColor } = calculateDeadlineProgress({
    scheduledDate: row.scheduled_date,
    dueDate: row.due_date,
    completionDate: row.completion_date,
  });

  const formattedDueDate = dueDate.toLocaleDateString();
  const formattedCompletionDate = completionDate?.toLocaleDateString();
  const tooltipLabel = completionDate
    ? `${t('tasks:data_grid.deadline_cell.completed')}: ${formattedCompletionDate}`
    : `${t('tasks:data_grid.deadline_cell.due')}: ${formattedDueDate}`;
  const tooltipText = `${tooltipLabel} (${t('tasks:data_grid.deadline_cell.elapsed', { percent: progressPercent.toFixed(0) })})`;

  return (
    <Tooltip title={tooltipText}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 6, borderRadius: 10, '& .MuiLinearProgress-bar': { bgcolor: barColor } }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {progressPercent.toFixed(0)}%
        </Typography>
      </Box>
    </Tooltip>
  );
}
